import { GQLAPIContextType } from '@awais4123/fillify-design-library/lib/common.types';
import { LocationOptionType } from 'components/LocationFilterInput/LocationFilterInput.types';
import { useSSFBehaviors } from 'hooks';
import { useUserTypeSwitch } from 'hooks/useUserTypeSwitch';
import { nonPaginatedApolloClient } from 'providers/ApolloClients/NonPaginatedApolloClient';
import { QUERY_fetchAgencyCommunities, QUERY_fetchSecondaryCommunities } from 'queries';
import { FC, useContext, useMemo } from 'react';
import { useUserAgencyIdSelector } from 'store/selectors/userSelectors/userSelector';
import { FetchAgencyCommunitiesQuery, FetchSecondaryCommunitiesQuery } from 'types/graphql';
import { createGQLDataContext } from 'utils/createDataContext';

export const SecondaryLocationFilterDataContext = createGQLDataContext({} as GQLAPIContextType);

const client = nonPaginatedApolloClient();

export const SecondaryLocationFilterDataProvider: FC = ({ children }) => {
  const agencyId = useUserAgencyIdSelector();

  const [values, useQueryOptions] = useSSFBehaviors<
    { data: FetchSecondaryCommunitiesQuery | FetchAgencyCommunitiesQuery },
    LocationOptionType[]
  >(
    useUserTypeSwitch({
      Agency: {
        key: 'LOCATION_FILTER_INPUT_AGENCY',
        query: QUERY_fetchAgencyCommunities,
        client,
        variables: {
          id: agencyId,
          byName: '',
        },
        pageSize: 20,
        debounceTimeout: 500,
        paginationType: 'InfiniteScroll',
        responseTransformer: (data?: FetchAgencyCommunitiesQuery) => {
          if (!data) return [];

          const mapped: LocationOptionType[] = data?.fetchAgency?.agencyCommunities
            ? data?.fetchAgency?.agencyCommunities.map((community) => {
                const stringified = community!.id.toString();
                return {
                  latitude: community.location?.latitude,
                  longitude: community.location?.longitude,
                  name: 'location',
                  label: community!.name,
                  key: stringified,
                  value: stringified,
                  id: stringified,
                  type: 'Standard',
                  timeZone: community.location?.timeZone || undefined,
                };
              })
            : [];
          return {
            options: mapped,
            hasNextPage: false,
          };
        },
      },
      B2B: {
        key: 'LOCATION_FILTER_INPUT_B2B',
        query: QUERY_fetchSecondaryCommunities,
        client,
        variables: {
          byName: '',
        },
        pageSize: 20,
        debounceTimeout: 500,
        paginationType: 'InfiniteScroll',
        responseTransformer: (data?: FetchSecondaryCommunitiesQuery) => {
          if (!data) return [];
          const mapped: LocationOptionType[] = data?.fetchCommunities?.nodes
            ? data.fetchCommunities.nodes.map(({ id, name, location }) => {
                const stringified = id.toString();
                return {
                  name: 'location',
                  label: name,
                  key: stringified,
                  value: stringified,
                  id: stringified,
                  type: 'Standard',
                  timeZone: location?.timeZone || undefined,
                };
              })
            : [];

          return {
            options: mapped,
            hasNextPage: data?.fetchCommunities?.pageInfo?.hasNextPage || false,
          };
        },
      },
    }),
  );

  const value = useMemo(() => {
    return { values, ...useQueryOptions };
  }, [values, useQueryOptions]);
  return (
    <SecondaryLocationFilterDataContext.Provider value={value}>
      {children}
    </SecondaryLocationFilterDataContext.Provider>
  );
};

export const useSecondaryLocationFilterDataContext = (): GQLAPIContextType<{
  options: LocationOptionType[];
  hasNextPage: boolean;
}> => useContext(SecondaryLocationFilterDataContext);
